import { useCallback, useRef } from 'react';
import { bankByIban, normalizeIban } from '../constants/kzBanks';

/**
 * Подставляет банк и БИК по введённому IBAN: код банка сидит в самом номере
 * счёта (знаки 5–7), и менеджеру нет смысла искать эти реквизиты руками.
 *
 * Возвращает обработчик для onValuesChange формы. Подставленное менеджер может
 * поправить — исправленное мы больше не трогаем, даже если IBAN потом поменяют.
 *
 * @param {Object} formRef — ref на HookForm (нужен его setValue)
 * @param {Object} fields — имена полей, если они отличаются от iban/bankName/bic
 */
const useIbanAutofill = (formRef, { iban = 'iban', bank = 'bankName', bic = 'bic' } = {}) => {
  const applied = useRef({ iban: null, bank: null, bic: null });

  return useCallback(
    (values) => {
      const current = normalizeIban(values?.[iban]);
      // setValue ниже снова дёрнет onValuesChange — выходим, пока счёт тот же.
      if (current === applied.current.iban) return;

      const found = bankByIban(current);
      if (!found) {
        applied.current = { iban: current, bank: null, bic: null };
        return;
      }

      const form = formRef?.current;
      if (!form) return;

      const bankValue = values?.[bank];
      const bicValue = values?.[bic];
      if (!bankValue || bankValue === applied.current.bank) form.setValue(bank, found.name);
      if (!bicValue || bicValue === applied.current.bic) form.setValue(bic, found.bic);

      applied.current = { iban: current, bank: found.name, bic: found.bic };
    },
    [formRef, iban, bank, bic]
  );
};

export default useIbanAutofill;
